import { useEffect, useRef, useState } from "react";
import { GROUPS, type ColumnDef } from "./columns";

/** "Columns · 9 of 27": the workbook is wider than any screen, so each viewer
 * picks the columns they read. The list is grouped the way the office thinks
 * of a job -- where it is, what paper it has, what it earned -- and closes on
 * a click outside it or Escape, like any menu. */
export function ColumnPicker({
  columns,
  visible,
  onToggle,
  onReset,
}: {
  columns: readonly ColumnDef[];
  visible: readonly string[];
  onToggle: (key: string) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const shown = columns.filter((column) => visible.includes(column.key)).length;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function onPointer(event: MouseEvent) {
      if (root.current !== null && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="column-picker" ref={root}>
      <button
        type="button"
        className="btn"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        Columns · {shown} of {columns.length}
      </button>
      {open ? (
        <div className="column-menu" role="group" aria-label="Columns to show">
          {GROUPS.map((group) => {
            const inGroup = columns.filter((column) => column.group === group);
            if (inGroup.length === 0) {
              return null;
            }
            return (
              <fieldset key={group} className="column-group">
                <legend className="section-label">{group}</legend>
                {inGroup.map((column) => (
                  <label key={column.key} className="column-option" title={column.key}>
                    <input
                      type="checkbox"
                      checked={visible.includes(column.key)}
                      onChange={() => onToggle(column.key)}
                    />
                    {column.label}
                  </label>
                ))}
              </fieldset>
            );
          })}
          <button type="button" className="go-link column-reset" onClick={onReset}>
            Back to the usual columns
          </button>
        </div>
      ) : null}
    </div>
  );
}
